import { useEffect, ReactNode, useState, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

 interface AnalyticsEvent {
   timestamp: number;
   action: string;
   category: string;
   label?: string;
   value?: number;
   payload?: any;
   status?: "sent" | "failed";
   error?: string;
 }
 
 const AnalyticsContext = createContext<{
   events: AnalyticsEvent[];
   addEvent?: (event: AnalyticsEvent) => void;
   trackInternal: (action: string, category: string, label?: string, value?: number, extraData?: any) => Promise<void>;
 }>({
   events: [],
   addEvent: () => {},
   trackInternal: async () => {},
 });

export const useAnalyticsDebug = () => useContext(AnalyticsContext);

export const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const [events, setEvents] = useState<AnalyticsEvent[]>([]);

   useEffect(() => {
     const handleEvent = (e: any) => {
       const event = e.detail as AnalyticsEvent;
       setEvents((prev) => [event, ...prev].slice(0, 50));
       
       // Email Alert for failures in debug/dev mode
       if (event.status === "failed" && import.meta.env.DEV) {
         supabase.functions.invoke("notify-analytics-error", {
           body: { event, environment: "development" }
         });
       }
     };
 
     window.addEventListener("analytics-event", handleEvent);
     return () => window.removeEventListener("analytics-event", handleEvent);
   }, []);
 
   const trackInternal = async (action: string, category: string, label?: string, value?: number, extraData?: any) => {
     try {
       const { data: { user } } = await supabase.auth.getUser();
       const { error } = await supabase.from("analytics_events").insert({
         action,
         category,
         label,
         value,
         payload: extraData,
         user_id: user?.id,
         session_id: window.sessionStorage.getItem("analytics_session_id") || undefined
       });
       if (error) throw error;
     } catch (err) {
       console.error("Error saving internal analytics event:", err);
     }
   };

  const { data: settings } = useQuery({
    queryKey: ["site-settings-tracking"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("ga_id, gtm_id")
        .eq("key", "global")
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  // Initialize GTM
  useEffect(() => {
    if (!settings?.gtm_id) return;

    const gtmId = settings.gtm_id;
    // Strict format validation to prevent script injection via admin-controlled value
    if (!/^GTM-[A-Z0-9]{4,10}$/.test(gtmId)) {
      console.warn("[Analytics] Invalid GTM ID format; refusing to inject.");
      return;
    }
    const script = document.createElement("script");
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;
    document.head.appendChild(script);

    const noscript = document.createElement("noscript");
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  }, [settings?.gtm_id]);

   // Retry logic and Queue processing
   useEffect(() => {
     const processQueues = async () => {
       // 1. Process DB Sync Queue
       const dbQueue = JSON.parse(localStorage.getItem("analytics_queue") || "[]");
       if (dbQueue.length > 0) {
         const { error } = await supabase.from("analytics_events").insert(
           dbQueue.map((e: any) => ({
             action: e.action,
             category: e.category,
             label: e.label,
             value: e.value,
             payload: e.payload,
             status: e.status,
             error: e.error,
             created_at: new Date(e.timestamp).toISOString()
           }))
         );
         if (!error) localStorage.removeItem("analytics_queue");
       }
 
       // 2. Process GA4/GTM Retry Queue
       if (window.gtag || window.dataLayer) {
         const retryQueue = JSON.parse(localStorage.getItem("analytics_retry_queue") || "[]");
         if (retryQueue.length > 0) {
           const stillFailed: any[] = [];
           for (const event of retryQueue) {
             try {
               if (window.gtag) {
                 window.gtag("event", event.action, {
                   event_category: event.category,
                   event_label: event.label,
                   value: event.value,
                   ...event.payload,
                   retransmitted: true
                 });
               }
               if (window.dataLayer) {
                 window.dataLayer.push({
                   event: "custom_event_retry",
                   event_action: event.action,
                   ...event.payload
                 });
               }
               // Update status in DB if it was previously failed
               await supabase.from("analytics_events")
                 .update({ status: "sent", error: null })
                 .eq("action", event.action)
                 .eq("created_at", new Date(event.timestamp).toISOString());
 
             } catch (err) {
               stillFailed.push(event);
             }
           }
           localStorage.setItem("analytics_retry_queue", JSON.stringify(stillFailed));
         }
       }
     };
 
     const interval = setInterval(processQueues, 30000); // Process every 30s
     processQueues(); // Also run on mount
     return () => clearInterval(interval);
   }, [settings?.ga_id]);
 
   // Initialize GA
   useEffect(() => {
     if (!settings?.ga_id) return;

    const gaId = settings.ga_id;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer?.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", gaId);
  }, [settings?.ga_id]);

  // Track page views
  useEffect(() => {
    const pagePath = location.pathname + location.search;
    if (settings?.ga_id && window.gtag) {
      window.gtag("event", "page_view", {
        page_path: pagePath,
      });
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        event: "pageview",
        page: pagePath,
      });
    }
    
    // Internal debug
     trackEvent("page_view", "Navigation", pagePath);
  }, [location, settings?.ga_id]);

   return (
     <AnalyticsContext.Provider value={{ events, trackInternal }}>
       {children}
     </AnalyticsContext.Provider>
   );
};

 export const trackEvent = (action: string, category: string, label?: string, value?: number, extraData?: any) => {
   // 1. Standardize Action Name (GA4 Recommended style)
   const mapping: Record<string, string> = {
     "registro": "sign_up",
     "login": "login",
     "verificacion": "verify_email",
     "crear_anuncio": "create_listing",
     "contacto": "generate_lead",
     "busqueda": "search",
     "filtro_aplicado": "apply_filter",
     "vista_producto": "view_item"
   };
   
   const rawAction = action.toLowerCase().replace(/\s+/g, '_');
   const standardizedAction = mapping[rawAction] || rawAction;
   
   let status: "sent" | "failed" = "sent";
   let error: string | undefined;
 
   if (import.meta.env.DEV) {
     console.log(`[Analytics] ${standardizedAction}`, { category, label, value, ...extraData });
   }
 
   try {
     // 2. Send to GA4
     if (window.gtag) {
       window.gtag("event", standardizedAction, {
         event_category: category,
         event_label: label,
         value: value,
         ...extraData,
       });
     }
     
     // 3. Send to GTM Data Layer
     if (window.dataLayer) {
       window.dataLayer.push({
         event: standardizedAction === "view_item" || standardizedAction === "sign_up" ? standardizedAction : "custom_event",
         event_action: standardizedAction,
         event_category: category,
         event_label: label,
         event_value: value,
         ...extraData,
       });
     }
   } catch (err: any) {
     status = "failed";
     error = err.message;
     console.error("[Analytics Error]", err);
   }
 
   // 4. Persistence and Retries
   const persistEvent = async () => {
     const eventData = {
       timestamp: Date.now(),
       action: standardizedAction,
       category,
       label,
       value,
       payload: extraData,
       status,
       error
     };
 
     // Save to session-based debug list
     window.dispatchEvent(new CustomEvent("analytics-event", { detail: eventData }));
     
     // Save to DB
     const { error: dbErr } = await supabase.from("analytics_events").insert({
       action: standardizedAction,
       category,
       label,
       value,
       payload: extraData,
       status: status,
       error: error
     });
 
     if (dbErr) {
       console.warn("Failed to save event to DB, adding to queue", dbErr);
       const queue = JSON.parse(localStorage.getItem("analytics_queue") || "[]");
       queue.push(eventData);
       localStorage.setItem("analytics_queue", JSON.stringify(queue.slice(-100))); // Limit queue
     }
 
     // If it failed to send to GA4/GTM, add to offline retry queue
     if (status === "failed") {
       const retryQueue = JSON.parse(localStorage.getItem("analytics_retry_queue") || "[]");
       retryQueue.push(eventData);
       localStorage.setItem("analytics_retry_queue", JSON.stringify(retryQueue.slice(-50)));
     }
   };
 
   persistEvent();
 };
